export const canNativeCaption = () =>
  typeof window !== 'undefined' && typeof window.workloopRealm?.transcribeWav === 'function';

export const canLiveNativeCaption = () =>
  typeof window !== 'undefined'
  && typeof window.workloopRealm?.startLiveCaption === 'function'
  && typeof window.workloopRealm?.pushLivePcm === 'function';

export const resampleTo16k = (input, fromRate) => {
  const rate = Number(fromRate) || 16000;
  if (!input?.length) return input;
  if (Math.abs(rate - 16000) < 1) return input;
  const outLen = Math.max(1, Math.round(input.length * 16000 / rate));
  const out = new Float32Array(outLen);
  const ratio = (input.length - 1) / Math.max(1, outLen - 1);
  for (let i = 0; i < outLen; i += 1) {
    const x = i * ratio;
    const i0 = Math.floor(x);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = x - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
};

export const mergeFloat32 = (chunks) => {
  const total = chunks.reduce((sum, part) => sum + part.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  chunks.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

export const float32ToInt16Bytes = (float32) => {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32[i]));
    out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return new Uint8Array(out.buffer);
};

export const encodeWav = (float32, sampleRate) => {
  const frames = float32.length;
  const buffer = new ArrayBuffer(44 + frames * 2);
  const view = new DataView(buffer);
  const writeString = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + frames * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, frames * 2, true);
  let cursor = 44;
  for (let i = 0; i < frames; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(cursor, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    cursor += 2;
  }
  return buffer;
};

export const closeAudioContext = (context) => {
  if (!context || context.state === 'closed') return;
  try {
    const pending = context.close();
    if (pending && typeof pending.catch === 'function') {
      pending.catch(() => {});
    }
  } catch {
    // Windows Chromium throws if close() runs twice; ignore.
  }
};

export const createPcmTap = (stream, { onFloat32, shouldPushLive } = {}) => {
  if (!stream || typeof AudioContext === 'undefined') return null;
  const context = new AudioContext();
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(2048, 1, 1);
  const mute = context.createGain();
  mute.gain.value = 0;
  try { context.resume?.(); } catch { /* ignore */ }
  processor.onaudioprocess = (event) => {
    const copy = new Float32Array(event.inputBuffer.getChannelData(0));
    onFloat32?.(copy, context.sampleRate || 16000);
    if (shouldPushLive?.()) {
      try {
        window.workloopRealm?.pushLivePcm?.(
          float32ToInt16Bytes(resampleTo16k(copy, context.sampleRate || 16000)),
        );
      } catch {
        // ignore
      }
    }
  };
  source.connect(processor);
  processor.connect(mute);
  mute.connect(context.destination);
  return {
    context,
    source,
    processor,
    mute,
    disconnect() {
      try { processor.disconnect(); } catch { /* ignore */ }
      try { source.disconnect(); } catch { /* ignore */ }
      try { mute.disconnect(); } catch { /* ignore */ }
      try { closeAudioContext(context); } catch { /* ignore */ }
    },
  };
};
